import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Plus, Send, Trash2, MessageSquare, Bot, ChevronDown, ChevronUp,
  Loader2, Sparkles, Pin, PinOff, FolderPlus, Folder, FolderOpen,
  Zap, Brain, X, MoreHorizontal, RefreshCw, Paperclip
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');
const getHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });

function renderMarkdown(text) {
  if (!text) return '';
  const lines = text.split('\n');
  let result = [];
  let inOl = false;
  let inUl = false;
  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      result.push(`<p>${paragraphBuffer.join(' ')}</p>`);
      paragraphBuffer = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    // Inline formatting
    line = line
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="ai-code">$1</code>');

    // Headings
    if (/^### (.+)$/.test(line)) {
      flushParagraph(); if (inOl) { result.push('</ol>'); inOl = false; } if (inUl) { result.push('</ul>'); inUl = false; }
      result.push(line.replace(/^### (.+)$/, '<h3 class="ai-h3">$1</h3>')); continue;
    }
    if (/^## (.+)$/.test(line)) {
      flushParagraph(); if (inOl) { result.push('</ol>'); inOl = false; } if (inUl) { result.push('</ul>'); inUl = false; }
      result.push(line.replace(/^## (.+)$/, '<h2 class="ai-h2">$1</h2>')); continue;
    }
    if (/^# (.+)$/.test(line)) {
      flushParagraph(); if (inOl) { result.push('</ol>'); inOl = false; } if (inUl) { result.push('</ul>'); inUl = false; }
      result.push(line.replace(/^# (.+)$/, '<h1 class="ai-h1">$1</h1>')); continue;
    }

    // Ordered list: "1. text" or "31. text" or "1) text"
    const olMatch = line.match(/^(\d+)[.)]\s+(.+)$/);
    if (olMatch) {
      flushParagraph();
      if (inUl) { result.push('</ul>'); inUl = false; }
      if (!inOl) { result.push(`<ol start="${olMatch[1]}" class="ai-ol-list">`); inOl = true; }
      result.push(`<li>${olMatch[2]}</li>`); continue;
    }

    // Unordered list
    const ulMatch = line.match(/^[-*]\s+(.+)$/);
    if (ulMatch) {
      flushParagraph();
      if (inOl) { result.push('</ol>'); inOl = false; }
      if (!inUl) { result.push('<ul class="ai-ul-list">'); inUl = true; }
      result.push(`<li>${ulMatch[1]}</li>`); continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      flushParagraph(); if (inOl) { result.push('</ol>'); inOl = false; } if (inUl) { result.push('</ul>'); inUl = false; }
      result.push('<hr class="ai-hr"/>'); continue;
    }

    // Close lists on non-list content
    if (inOl) { result.push('</ol>'); inOl = false; }
    if (inUl) { result.push('</ul>'); inUl = false; }

    // Empty line = paragraph break
    if (line.trim() === '') { flushParagraph(); continue; }

    // Accumulate text into paragraph
    paragraphBuffer.push(line);
  }

  flushParagraph();
  if (inOl) result.push('</ol>');
  if (inUl) result.push('</ul>');
  return result.join('\n');
}

// Source card for deep mode
function SourceCard({ label, color, content, isOpen, onToggle }) {
  return (
    <div style={{ border: `1px solid ${color}22`, borderRadius: 8, background: `${color}08`, marginBottom: 4 }}>
      <button onClick={onToggle} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', fontSize: 11, fontWeight: 600, color: 'hsl(var(--text-secondary))', background: 'none', border: 'none', cursor: 'pointer' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
          {label}
        </span>
        {isOpen ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
      </button>
      {isOpen && (
        <div style={{ padding: '8px 10px', fontSize: 11.5, lineHeight: 1.6, color: 'hsl(var(--text-secondary))', borderTop: `1px solid ${color}15` }} dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />
      )}
    </div>
  );
}

function ChatMessage({ msg, isUser, isNew, onTogglePin }) {
  const [showSources, setShowSources] = useState(false);
  const [openSource, setOpenSource] = useState(null);

  if (isUser) {
    return (
      <div className={`ai-msg-row ai-msg-user ${isNew ? 'ai-msg-enter' : ''}`} data-testid="chat-message-user">
        <div className="ai-bubble-user"><p>{msg.content}</p></div>
      </div>
    );
  }

  const hasIndividual = msg.individual_responses && Object.keys(msg.individual_responses).length > 0;
  const sources = [
    { key: 'openai', label: 'Analysis A', color: '#10b981' },
    { key: 'gemini', label: 'Analysis B', color: '#3b82f6' },
    { key: 'claude', label: 'Analysis C', color: '#f97316' },
  ];

  return (
    <div className={`ai-msg-row ai-msg-bot ${isNew ? 'ai-msg-enter' : ''}`} data-testid="chat-message-assistant">
      <div className="ai-bot-avatar"><Bot size={13} /></div>
      <div className="ai-msg-wrap">
        <div className="ai-bubble-bot">
          <div className="ai-prose" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
        </div>
        <div className="ai-msg-actions">
          <button
            onClick={() => onTogglePin(msg.id)}
            className={`ai-action-btn ${msg.pinned ? 'ai-pinned' : ''}`}
            title={msg.pinned ? 'Unpin' : 'Pin'}
            data-testid={`pin-msg-${msg.id}`}
          >
            {msg.pinned ? <PinOff size={11} /> : <Pin size={11} />}
            {msg.pinned ? 'Pinned' : 'Pin'}
          </button>
          {msg.mode === 'deep' && <span className="ai-mode-badge-deep"><Brain size={9} /> Deep</span>}
          {hasIndividual && (
            <button onClick={() => setShowSources(!showSources)} className="ai-action-btn" data-testid="toggle-individual-responses">
              <Sparkles size={10} />
              {showSources ? 'Sembunyikan sumber' : 'Lihat analisis individu'}
            </button>
          )}
        </div>
        {showSources && hasIndividual && (
          <div style={{ marginTop: 6 }}>
            {sources.map(s => (
              <SourceCard
                key={s.key}
                label={s.label}
                color={s.color}
                content={msg.individual_responses[s.key]}
                isOpen={openSource === s.key}
                onToggle={() => setOpenSource(openSource === s.key ? null : s.key)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AiAgentPage() {
  const [sessions, setSessions] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [mode, setMode] = useState('fast');
  const [newMsgIdx, setNewMsgIdx] = useState(-1);
  const [showPinned, setShowPinned] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  // Pending files staged for the next Send action — lets user upload one or
  // more files AND type a follow-up question in the same input before
  // submitting. Backend accepts an array of files via multipart form.
  const [pendingFiles, setPendingFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { fetchSessions(); fetchFolders(); }, []);
  useEffect(() => { 
    // Scroll to bottom whenever messages change or loading state changes
    const timer = setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    return () => clearTimeout(timer);
  }, [messages, loading]);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API}/api/ai-agent/sessions`, { headers: getHeaders() });
      if (res.ok) setSessions((await res.json()).sessions || []);
    } catch {} finally { setSessionsLoading(false); }
  };

  const fetchFolders = async () => {
    try {
      const res = await fetch(`${API}/api/ai-agent/folders`, { headers: getHeaders() });
      if (res.ok) setFolders((await res.json()).folders || []);
    } catch {}
  };

  const loadSession = async (sessionId) => {
    setActiveSessionId(sessionId);
    setNewMsgIdx(-1);
    setShowPinned(false);
    try {
      const res = await fetch(`${API}/api/ai-agent/sessions/${sessionId}`, { headers: getHeaders() });
      if (res.ok) setMessages((await res.json()).messages || []);
    } catch {}
  };

  const startNewChat = () => { setActiveSessionId(null); setMessages([]); setNewMsgIdx(-1); setShowPinned(false); inputRef.current?.focus(); };

  const deleteSession = async (sessionId) => {
    try {
      await fetch(`${API}/api/ai-agent/sessions/${sessionId}`, { method: 'DELETE', headers: getHeaders() });
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (activeSessionId === sessionId) { setActiveSessionId(null); setMessages([]); }
      toast.success('Sesi dipadam');
    } catch { toast.error('Gagal padam sesi'); }
  };

  const togglePin = async (msgId) => {
    try {
      const res = await fetch(`${API}/api/ai-agent/messages/${msgId}/pin`, { method: 'PUT', headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => prev.map(m => m.id === msgId ? { ...m, pinned: data.pinned } : m));
        toast.success(data.pinned ? 'Mesej di-pin' : 'Pin dibuang');
      }
    } catch { toast.error('Gagal pin mesej'); }
  };

  const fetchPinned = async () => {
    try {
      const res = await fetch(`${API}/api/ai-agent/pinned`, { headers: getHeaders() });
      if (res.ok) setPinnedMessages((await res.json()).pinned_messages || []);
    } catch {}
  };

  const toggleShowPinned = () => {
    if (!showPinned) fetchPinned();
    setShowPinned(!showPinned);
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API}/api/ai-agent/folders`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ name }) });
      if (res.ok) { fetchFolders(); setNewFolderName(''); setShowNewFolder(false); toast.success('Folder dibuat'); }
    } catch { toast.error('Gagal buat folder'); }
  };

  const deleteFolder = async (folderId) => {
    try {
      await fetch(`${API}/api/ai-agent/folders/${folderId}`, { method: 'DELETE', headers: getHeaders() });
      fetchFolders(); fetchSessions(); toast.success('Folder dipadam');
    } catch { toast.error('Gagal padam folder'); }
  };

  const moveToFolder = async (sessionId, folderId) => {
    try {
      await fetch(`${API}/api/ai-agent/sessions/${sessionId}/folder`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify({ folder_id: folderId }) });
      fetchSessions(); setContextMenu(null); toast.success('Chat dipindahkan');
    } catch { toast.error('Gagal pindah chat'); }
  };

  const sendMessage = async (retryText) => {
    const text = (retryText || input).trim();
    // Allow send with pending files even if text is empty (user just wants
    // AI to analyse the files). Otherwise require non-empty text.
    if (!text && pendingFiles.length === 0) return;
    if (loading) return;
    if (!retryText) setInput('');
    setLoading(true);

    // Capture pendingFiles before clearing so state changes don't race the fetch
    const filesPayload = pendingFiles;
    if (filesPayload.length > 0 && !retryText) setPendingFiles([]);

    const displayText = filesPayload.length > 0
      ? `[Files: ${filesPayload.map(f => f.name).join(', ')}] ${text}`
      : text;
    const tempUserMsg = { id: 'temp-user', role: 'user', content: displayText, created_at: new Date().toISOString() };
    if (!retryText) setMessages(prev => [...prev, tempUserMsg]);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);
      let res, data;
      if (filesPayload.length > 0) {
        // File-upload path — multipart, may create a new session on the server side.
        // Uses `files` (repeated field) so backend can accept multiple attachments.
        const formData = new FormData();
        filesPayload.forEach(f => formData.append('files', f));
        formData.append('session_id', activeSessionId || '');
        const fileNames = filesPayload.map(f => f.name).join(', ');
        formData.append('message', text || `Analyze these files: ${fileNames}`);
        formData.append('mode', mode);
        res = await fetch(`${API}/api/ai-agent/upload-file`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formData,
          signal: controller.signal,
        });
      } else {
        res = await fetch(`${API}/api/ai-agent/chat`, {
          method: 'POST', headers: getHeaders(),
          body: JSON.stringify({ session_id: activeSessionId, message: text, mode }),
          signal: controller.signal
        });
      }
      clearTimeout(timeoutId);
      if (!res.ok) {
        // FastAPI returns `detail` as string OR array of validation objects.
        // Coerce properly so users don't see "[object Object]".
        const errJson = await res.json().catch(() => ({}));
        const detail = errJson.detail;
        let msg;
        if (typeof detail === 'string') msg = detail;
        else if (Array.isArray(detail)) {
          msg = detail.map(d => (typeof d === 'string' ? d : (d?.msg || JSON.stringify(d)))).join('; ');
        } else if (detail && typeof detail === 'object') {
          msg = detail.msg || JSON.stringify(detail);
        } else {
          msg = `Server error (${res.status})`;
        }
        throw new Error(msg);
      }
      data = await res.json();
      if (!activeSessionId) { setActiveSessionId(data.session_id); fetchSessions(); }
      
      // Update messages — keep setNewMsgIdx OUTSIDE the updater to avoid React 18 batching issues
      const userMsg = { id: `user-${Date.now()}`, role: 'user', content: displayText, created_at: tempUserMsg.created_at };
      setMessages(prev => [...prev.filter(m => m.id !== 'temp-user'), userMsg, data.message]);
      setNewMsgIdx(-1); // Reset first
      // Use requestAnimationFrame to ensure DOM updates before scroll
      requestAnimationFrame(() => {
        setNewMsgIdx(prev => prev); // Force re-check
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    } catch (err) {
      toast.error(err.name === 'AbortError' ? 'Timeout — cuba lagi.' : (err.message || 'Ralat'));
      setMessages(prev => prev.filter(m => m.id !== 'temp-user'));
      // Re-stage the files so user can retry without re-picking
      if (filesPayload.length > 0) setPendingFiles(filesPayload);
    } finally { 
      setLoading(false);
      // Double-check scroll after loading completes
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const allSuggestions = [
    'Channel mana paling perform bulan ini?',
    'Bandingkan marketing spend semua channel',
    'Apa masalah utama yang perlu diselesaikan?',
    'Cadangan strategi untuk improve sales',
    'Review content Telegram channel FIRA — apa kekuatan dan kelemahan?',
    'Audit copywriting channel public semua IB — mana yang paling engaging?',
    'Analisis win rate operation semua channel — mana perlu improve?',
    'Closing rate mana paling rendah dan apa cadangan?',
    'Channel mana yang ROI paling tinggi dari segi ad spend vs commission?',
    'Banding subscriber growth semua channel bulan ni vs bulan lepas',
    'Senarai channel yang tak capai target bulan ini',
    'Apa trend deposit dan commission keseluruhan?',
    'Review posting frequency semua Telegram channel — siapa paling aktif?',
    'Mana channel yang CPS paling tinggi dan macam mana nak kurangkan?',
    'Ranking channel berdasarkan health score — apa action plan?',
    'Analisis team productivity — siapa yang paling aktif submit standup?',
    'Ada fund request yang pending? Apa status?',
    'Cadangan content strategy untuk Telegram channel yang subscriber menurun',
    'Berapa banyak creative request pending dan siapa PIC?',
    'Compare bulan ni dengan bulan lepas — overall bisnes naik atau turun?',
  ];

  const [suggestionSeed, setSuggestionSeed] = useState(0);
  const suggestions = React.useMemo(() => {
    const shuffled = [...allSuggestions].sort(() => Math.sin(suggestionSeed * 100 + Math.random()) - 0.5);
    return shuffled.slice(0, 4);
  }, [suggestionSeed]);
  const refreshSuggestions = () => setSuggestionSeed(prev => prev + 1);

  // Organize sessions by folder
  const unfolderedSessions = sessions.filter(s => !s.folder_id);
  const folderSessions = (folderId) => sessions.filter(s => s.folder_id === folderId);

  const toggleFolder = (fid) => setExpandedFolders(prev => ({ ...prev, [fid]: !prev[fid] }));

  const renderSessionItem = (s) => (
    <div
      key={s.session_id}
      className={`ai-sess-item ${s.session_id === activeSessionId ? 'active' : ''}`}
      onClick={() => loadSession(s.session_id)}
      data-testid={`session-item-${s.session_id}`}
    >
      <MessageSquare size={12} style={{ flexShrink: 0, opacity: 0.5 }} />
      <span className="ai-sess-title">{s.title || 'Chat baru'}</span>
      <button className="ai-sess-more" onClick={(e) => { e.stopPropagation(); setContextMenu(contextMenu === s.session_id ? null : s.session_id); }} data-testid={`session-more-${s.session_id}`}>
        <MoreHorizontal size={12} />
      </button>
      {contextMenu === s.session_id && (
        <div className="ai-ctx-menu" onClick={(e) => e.stopPropagation()}>
          {folders.map(f => (
            <button key={f.id} className="ai-ctx-item" onClick={() => moveToFolder(s.session_id, f.id)}>
              <Folder size={11} /> {f.name}
            </button>
          ))}
          {s.folder_id && (
            <button className="ai-ctx-item" onClick={() => moveToFolder(s.session_id, null)}>
              <X size={11} /> Keluarkan dari folder
            </button>
          )}
          <button className="ai-ctx-item ai-ctx-delete" onClick={() => { deleteSession(s.session_id); setContextMenu(null); }}>
            <Trash2 size={11} /> Padam
          </button>
        </div>
      )}
    </div>
  );

  return (
    <ResponsiveLayout>
      <style>{`
        .ai-root { display:flex; height:calc(100vh - 64px); overflow:hidden; background:hsl(var(--bg-primary)); }

        /* LEFT PANEL */
        .ai-left { width:260px; min-width:260px; display:flex; flex-direction:column; background:hsl(var(--surface-base)); }
        .ai-left-top { padding:14px; border-bottom:1px solid hsl(var(--border-primary)); }
        .ai-new-btn { width:100%; display:flex; align-items:center; justify-content:center; gap:7px; padding:10px; border-radius:10px; font-size:12.5px; font-weight:600; color:#fff; border:none; cursor:pointer; background:linear-gradient(135deg,#7c3aed,#4f46e5); transition:all .15s; }
        .ai-new-btn:hover { box-shadow:0 3px 14px rgba(124,58,237,.35); transform:translateY(-1px); }
        .ai-left-scroll { flex:1; overflow-y:auto; padding:6px 8px; }

        /* FOLDERS */
        .ai-folder-row { display:flex; align-items:center; gap:6px; padding:6px 8px; font-size:11px; font-weight:600; color:hsl(var(--text-tertiary)); cursor:pointer; border-radius:6px; margin-bottom:2px; }
        .ai-folder-row:hover { background:hsl(var(--surface-overlay)); color:hsl(var(--text-secondary)); }
        .ai-folder-del { opacity:0; background:none; border:none; cursor:pointer; color:hsl(var(--text-tertiary)); padding:2px; }
        .ai-folder-row:hover .ai-folder-del { opacity:1; }
        .ai-folder-del:hover { color:#ef4444; }
        .ai-folder-children { margin-left:12px; border-left:1px solid hsl(var(--border-primary)); padding-left:6px; margin-bottom:4px; }
        .ai-new-folder-row { display:flex; gap:4px; padding:4px 8px; margin-bottom:4px; }
        .ai-new-folder-row input { flex:1; font-size:11px; padding:4px 8px; border-radius:6px; border:1px solid hsl(var(--border-primary)); background:hsl(var(--surface-elevated)); color:hsl(var(--text-primary)); outline:none; }
        .ai-new-folder-row input:focus { border-color:rgba(124,58,237,.5); }

        /* SESSION ITEMS */
        .ai-sess-item { display:flex; align-items:center; gap:7px; padding:8px 10px; border-radius:8px; cursor:pointer; transition:all .12s; margin-bottom:1px; position:relative; color:hsl(var(--text-secondary)); font-size:11.5px; }
        .ai-sess-item:hover { background:hsl(var(--surface-overlay)); }
        .ai-sess-item.active { background:rgba(124,58,237,.12); color:hsl(var(--text-primary)); }
        .ai-sess-title { flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .ai-sess-more { opacity:0; background:none; border:none; cursor:pointer; color:hsl(var(--text-tertiary)); padding:2px; }
        .ai-sess-item:hover .ai-sess-more { opacity:1; }

        /* CONTEXT MENU */
        .ai-ctx-menu { position:absolute; right:0; top:100%; z-index:50; background:hsl(var(--surface-elevated)); border:1px solid hsl(var(--border-primary)); border-radius:8px; padding:4px; min-width:160px; box-shadow:0 8px 24px rgba(0,0,0,.3); }
        .ai-ctx-item { display:flex; align-items:center; gap:6px; width:100%; padding:6px 10px; font-size:11px; color:hsl(var(--text-secondary)); background:none; border:none; cursor:pointer; border-radius:5px; text-align:left; }
        .ai-ctx-item:hover { background:hsl(var(--surface-overlay)); }
        .ai-ctx-delete { color:#ef4444; }

        /* PINNED SECTION */
        .ai-pin-toggle { display:flex; align-items:center; gap:5px; padding:8px 10px; font-size:10.5px; font-weight:600; color:hsl(var(--text-tertiary)); cursor:pointer; background:none; border:none; width:100%; text-transform:uppercase; letter-spacing:.5px; border-top:1px solid hsl(var(--border-primary)); }
        .ai-pin-toggle:hover { color:#a78bfa; }
        .ai-pinned-list { padding:4px 8px; max-height:200px; overflow-y:auto; border-top:1px solid hsl(var(--border-primary)); }
        .ai-pinned-item { padding:6px 8px; font-size:10.5px; color:hsl(var(--text-secondary)); border-radius:6px; cursor:pointer; margin-bottom:2px; line-height:1.4; display:flex; align-items:flex-start; gap:5px; }
        .ai-pinned-item:hover { background:hsl(var(--surface-overlay)); }

        /* ============ SEPARATOR ============ */
        .ai-separator { width:1px; background:hsl(var(--border-primary)); flex-shrink:0; position:relative; }
        .ai-separator::after { content:''; position:absolute; top:0; bottom:0; left:-1px; width:3px; background:linear-gradient(180deg, rgba(124,58,237,0.4) 0%, rgba(124,58,237,0.1) 30%, rgba(124,58,237,0.1) 70%, rgba(124,58,237,0.4) 100%); }

        /* RIGHT PANEL */
        .ai-right { flex:1; display:flex; flex-direction:column; min-width:0; background:hsl(var(--bg-primary)); border-left:1px solid hsl(var(--border-primary)); }
        .ai-header { padding:12px 24px; border-bottom:1px solid hsl(var(--border-primary)); display:flex; align-items:center; justify-content:space-between; background:hsl(var(--surface-base)); }
        .ai-header-left { display:flex; align-items:center; gap:12px; }
        .ai-header-avatar { width:36px; height:36px; border-radius:12px; display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,#7c3aed,#4f46e5); box-shadow:0 3px 12px rgba(124,58,237,.3); }
        .ai-header-title { font-size:14px; font-weight:700; color:hsl(var(--text-primary)); }
        .ai-header-sub { font-size:10px; color:hsl(var(--text-tertiary)); margin-top:1px; }

        /* MODE TOGGLE */
        .ai-mode-toggle { display:flex; align-items:center; background:hsl(var(--surface-elevated)); border-radius:8px; border:1px solid hsl(var(--border-primary)); overflow:hidden; }
        .ai-mode-btn { display:flex; align-items:center; gap:4px; padding:6px 12px; font-size:10.5px; font-weight:600; border:none; cursor:pointer; transition:all .15s; background:transparent; color:hsl(var(--text-tertiary)); }
        .ai-mode-btn.active-fast { background:linear-gradient(135deg,#10b981,#059669); color:#fff; }
        .ai-mode-btn.active-deep { background:linear-gradient(135deg,#7c3aed,#4f46e5); color:#fff; }

        /* MESSAGES */
        .ai-messages { flex:1; overflow-y:auto; padding:20px 24px; }
        .ai-msg-row { display:flex; margin-bottom:16px; animation:aiFadeIn .3s ease; }
        .ai-msg-enter { animation:aiSlideIn .35s ease; }
        @keyframes aiFadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes aiSlideIn { from{opacity:0;transform:translateY(14px) scale(.98)} to{opacity:1;transform:translateY(0) scale(1)} }
        .ai-msg-user { justify-content:flex-end; }
        .ai-msg-bot { justify-content:flex-start; gap:8px; }
        .ai-bubble-user { max-width:70%; padding:10px 16px; border-radius:16px 16px 4px 16px; background:linear-gradient(135deg,#7c3aed,#4f46e5); color:#fff; font-size:13px; line-height:1.6; box-shadow:0 2px 10px rgba(124,58,237,.2); }
        .ai-bubble-user p { margin:0; white-space:pre-wrap; }
        .ai-bot-avatar { width:28px; height:28px; min-width:28px; border-radius:9px; display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,#7c3aed,#4f46e5); color:#fff; margin-top:2px; }
        .ai-msg-wrap { max-width:80%; min-width:0; }
        .ai-bubble-bot { padding:16px 20px; border-radius:4px 16px 16px 16px; background:hsl(var(--surface-elevated)); border:1px solid hsl(var(--border-primary)); }
        .ai-prose { font-size:13.5px; line-height:1.8; color:hsl(var(--text-primary)); word-wrap:break-word; }
        .ai-prose > br { display:block; content:''; margin-top:10px; }
        .ai-prose p { margin:0 0 12px; }
        .ai-prose strong { font-weight:700; color:hsl(var(--text-primary)); }
        .ai-prose em { font-style:italic; color:hsl(var(--text-secondary)); }
        .ai-prose .ai-code { background:rgba(124,58,237,.12); padding:2px 6px; border-radius:4px; font-size:12px; font-family:'IBM Plex Mono',monospace; color:#c4b5fd; }
        .ai-prose .ai-h1 { font-size:20px; font-weight:800; margin:24px 0 10px; color:hsl(var(--text-primary)); letter-spacing:-0.3px; line-height:1.3; border-bottom:1px solid hsl(var(--border-primary)); padding-bottom:8px; }
        .ai-prose .ai-h2 { font-size:17px; font-weight:700; margin:20px 0 8px; color:hsl(var(--text-primary)); letter-spacing:-0.2px; line-height:1.35; }
        .ai-prose .ai-h3 { font-size:14.5px; font-weight:700; margin:16px 0 6px; color:hsl(var(--text-primary)); line-height:1.4; }
        .ai-prose .ai-ol-list { margin:10px 0 14px; padding-left:24px; list-style:decimal; }
        .ai-prose .ai-ol-list li { margin-bottom:6px; padding-left:4px; line-height:1.7; }
        .ai-prose .ai-ul-list { margin:10px 0 14px; padding-left:20px; list-style:disc; }
        .ai-prose .ai-ul-list li { margin-bottom:6px; padding-left:4px; line-height:1.7; }
        .ai-prose .ai-ul-list li::marker { color:rgba(124,58,237,.6); }
        .ai-prose .ai-ol-list li::marker { color:rgba(124,58,237,.7); font-weight:600; }
        .ai-prose .ai-hr { border:none; border-top:1px solid hsl(var(--border-primary)); margin:18px 0; }

        /* MSG ACTIONS */
        .ai-msg-actions { display:flex; align-items:center; gap:6px; margin-top:5px; flex-wrap:wrap; }
        .ai-action-btn { display:flex; align-items:center; gap:3px; font-size:10px; color:hsl(var(--text-tertiary)); background:none; border:none; cursor:pointer; padding:2px 6px; border-radius:4px; transition:all .12s; }
        .ai-action-btn:hover { color:#a78bfa; background:rgba(124,58,237,.08); }
        .ai-pinned { color:#f59e0b !important; }
        .ai-mode-badge-deep { display:flex; align-items:center; gap:3px; font-size:9px; font-weight:600; color:#a78bfa; background:rgba(124,58,237,.1); padding:2px 6px; border-radius:4px; }

        /* WELCOME */
        .ai-welcome { display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; text-align:center; }
        .ai-welcome-orb { width:64px; height:64px; border-radius:20px; display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,rgba(124,58,237,.2),rgba(79,70,229,.15)); margin-bottom:16px; animation:aiFloat 4s ease-in-out infinite; }
        @keyframes aiFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
        .ai-welcome h3 { font-size:18px; font-weight:800; color:hsl(var(--text-primary)); margin-bottom:4px; }
        .ai-welcome .ai-sub { font-size:11.5px; color:hsl(var(--text-tertiary)); max-width:360px; line-height:1.5; margin-bottom:24px; }
        .ai-sug-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; max-width:440px; width:100%; }
        .ai-sug-card { text-align:left; padding:12px 14px; border-radius:12px; border:1px solid hsl(var(--border-primary)); background:hsl(var(--surface-elevated)); color:hsl(var(--text-secondary)); font-size:11.5px; line-height:1.4; cursor:pointer; transition:all .15s; }
        .ai-sug-card:hover { border-color:rgba(124,58,237,.3); transform:translateY(-1px); box-shadow:0 4px 12px rgba(0,0,0,.12); }
        .ai-refresh-btn { display:flex; align-items:center; gap:6px; margin-top:12px; padding:8px 16px; border-radius:20px; border:1px solid hsl(var(--border-primary)); background:transparent; color:hsl(var(--text-tertiary)); font-size:11px; cursor:pointer; transition:all .15s; }
        .ai-refresh-btn:hover { border-color:rgba(124,58,237,.4); color:rgba(124,58,237,.8); background:rgba(124,58,237,.05); }
        .ai-refresh-btn:hover svg { transform:rotate(180deg); }
        .ai-refresh-btn svg { transition:transform .3s ease; }

        /* LOADING */
        .ai-loading-inner { padding:12px 18px; border-radius:4px 16px 16px 16px; background:hsl(var(--surface-elevated)); border:1px solid hsl(var(--border-primary)); }
        .ai-typing-dots { display:inline-flex; gap:3px; align-items:center; }
        .ai-typing-dots span { width:5px; height:5px; border-radius:50%; background:#7c3aed; animation:aiDot 1.4s ease-in-out infinite; }
        .ai-typing-dots span:nth-child(2) { animation-delay:.2s; }
        .ai-typing-dots span:nth-child(3) { animation-delay:.4s; }
        @keyframes aiDot { 0%,80%,100%{transform:scale(.6);opacity:.4} 40%{transform:scale(1);opacity:1} }
        .ai-shimmer { height:8px; border-radius:4px; background:linear-gradient(90deg,hsl(var(--surface-elevated)) 25%,rgba(124,58,237,.08) 50%,hsl(var(--surface-elevated)) 75%); background-size:200% 100%; animation:aiShimmer 1.5s ease-in-out infinite; margin-bottom:5px; }
        @keyframes aiShimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }

        /* INPUT */
        .ai-input-section { padding:14px 24px; border-top:1px solid hsl(var(--border-primary)); background:hsl(var(--surface-base)); }
        .ai-input-wrap { display:flex; align-items:flex-end; gap:8px; max-width:780px; margin:0 auto; }
        .ai-input-field { flex:1; resize:none; background:hsl(var(--surface-elevated)); border:1px solid hsl(var(--border-primary)); border-radius:12px; padding:10px 14px; font-size:13px; color:hsl(var(--text-primary)); outline:none; transition:all .2s; min-height:42px; max-height:120px; font-family:inherit; }
        .ai-input-field::placeholder { color:hsl(var(--text-tertiary)); }
        .ai-input-field:focus { border-color:rgba(124,58,237,.5); box-shadow:0 0 0 3px rgba(124,58,237,.08); }
        .ai-send-btn { width:42px; height:42px; min-width:42px; border-radius:12px; display:flex; align-items:center; justify-content:center; border:none; cursor:pointer; background:linear-gradient(135deg,#7c3aed,#4f46e5); color:#fff; transition:all .15s; }
        .ai-send-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 3px 12px rgba(124,58,237,.35); }
        .ai-send-btn:disabled { opacity:.3; cursor:default; }

        @media (max-width:768px) { .ai-left,.ai-separator { display:none; } }
      `}</style>

      <div className="ai-root" data-testid="ai-agent-page" onClick={() => { if (contextMenu) setContextMenu(null); }}>
        {/* LEFT PANEL */}
        <div className="ai-left">
          <div className="ai-left-top">
            <button className="ai-new-btn" onClick={startNewChat} data-testid="new-chat-btn">
              <Plus size={14} /> Chat Baru
            </button>
          </div>
          <div className="ai-left-scroll">
            {/* Folder create */}
            <div style={{ padding: '4px 0', marginBottom: 4 }}>
              {showNewFolder ? (
                <div className="ai-new-folder-row">
                  <input
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                    placeholder="Nama folder..."
                    autoFocus
                    data-testid="new-folder-input"
                  />
                  <button onClick={createFolder} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981' }}><Plus size={14} /></button>
                  <button onClick={() => setShowNewFolder(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--text-tertiary))' }}><X size={14} /></button>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewFolder(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px', fontSize: 10, color: 'hsl(var(--text-tertiary))', background: 'none', border: 'none', cursor: 'pointer' }}
                  data-testid="add-folder-btn"
                >
                  <FolderPlus size={12} /> Folder baru
                </button>
              )}
            </div>

            {/* Folders */}
            {folders.map(f => (
              <div key={f.id}>
                <div className="ai-folder-row" onClick={() => toggleFolder(f.id)}>
                  {expandedFolders[f.id] ? <FolderOpen size={13} /> : <Folder size={13} />}
                  <span style={{ flex: 1 }}>{f.name}</span>
                  <span style={{ fontSize: 9, opacity: 0.5 }}>{folderSessions(f.id).length}</span>
                  <button className="ai-folder-del" onClick={(e) => { e.stopPropagation(); deleteFolder(f.id); }} data-testid={`delete-folder-${f.id}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
                {expandedFolders[f.id] && (
                  <div className="ai-folder-children">
                    {folderSessions(f.id).length === 0 ? (
                      <p style={{ fontSize: 10, color: 'hsl(var(--text-tertiary))', padding: '4px 8px' }}>Kosong</p>
                    ) : folderSessions(f.id).map(renderSessionItem)}
                  </div>
                )}
              </div>
            ))}

            {/* Unfoldered sessions */}
            {sessionsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <Loader2 size={14} className="animate-spin" style={{ color: 'hsl(var(--text-tertiary))' }} />
              </div>
            ) : unfolderedSessions.length === 0 && folders.length === 0 ? (
              <p style={{ fontSize: 10.5, color: 'hsl(var(--text-tertiary))', textAlign: 'center', padding: '24px 8px' }}>Tiada sesi lagi</p>
            ) : unfolderedSessions.map(renderSessionItem)}
          </div>

          {/* Pinned section */}
          <div>
            <button className="ai-pin-toggle" onClick={toggleShowPinned} data-testid="pinned-toggle">
              <Pin size={11} /> Pinned ({pinnedMessages.length || 0})
              {showPinned ? <ChevronUp size={10} style={{ marginLeft: 'auto' }} /> : <ChevronDown size={10} style={{ marginLeft: 'auto' }} />}
            </button>
            {showPinned && (
              <div className="ai-pinned-list">
                {pinnedMessages.length === 0 ? (
                  <p style={{ fontSize: 10, color: 'hsl(var(--text-tertiary))', textAlign: 'center', padding: 8 }}>Tiada mesej di-pin</p>
                ) : pinnedMessages.map(p => (
                  <div key={p.id} className="ai-pinned-item" onClick={() => p.session_id && loadSession(p.session_id)}>
                    <Pin size={10} style={{ color: '#f59e0b', marginTop: 2, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 9, color: 'hsl(var(--text-tertiary))', marginBottom: 2 }}>{p.session_title}</div>
                      {p.content?.substring(0, 80)}...
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* SEPARATOR */}
        <div className="ai-separator" />

        {/* RIGHT PANEL */}
        <div className="ai-right">
          {/* Header */}
          <div className="ai-header">
            <div className="ai-header-left">
              <div className="ai-header-avatar"><Bot size={16} color="#fff" /></div>
              <div>
                <div className="ai-header-title" data-testid="ai-agent-title">UA AI Agent</div>
                <div className="ai-header-sub">Penasihat strategik CRM anda</div>
              </div>
            </div>
            <div className="ai-mode-toggle" data-testid="mode-toggle">
              <button className={`ai-mode-btn ${mode === 'fast' ? 'active-fast' : ''}`} onClick={() => setMode('fast')} data-testid="mode-fast">
                <Zap size={11} /> Quick
              </button>
              <button className={`ai-mode-btn ${mode === 'deep' ? 'active-deep' : ''}`} onClick={() => setMode('deep')} data-testid="mode-deep">
                <Brain size={11} /> Deep Analysis
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="ai-messages" data-testid="chat-messages-area">
            {messages.length === 0 && !loading ? (
              <div className="ai-welcome">
                <div className="ai-welcome-orb"><Bot size={26} color="#a78bfa" /></div>
                <h3>UA AI Agent</h3>
                <p className="ai-sub">Tanya apa sahaja tentang bisnes, channel, marketing, sales, atau strategi anda.</p>
                <div className="ai-sug-grid" data-testid="suggestion-cards">
                  {suggestions.map((s, i) => (
                    <button key={`${suggestionSeed}-${i}`} className="ai-sug-card" onClick={() => { setInput(s); inputRef.current?.focus(); }} data-testid={`suggestion-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
                <button
                  className="ai-refresh-btn"
                  onClick={refreshSuggestions}
                  data-testid="refresh-suggestions-btn"
                >
                  <RefreshCw size={14} />
                  Lagi soalan
                </button>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <ChatMessage key={msg.id || idx} msg={msg} isUser={msg.role === 'user'} isNew={idx === newMsgIdx} onTogglePin={togglePin} />
                ))}
                {loading && (
                  <div className="ai-msg-row ai-msg-bot ai-msg-enter" data-testid="loading-indicator">
                    <div className="ai-bot-avatar"><Bot size={13} color="#fff" /></div>
                    <div style={{ maxWidth: '60%' }}>
                      <div className="ai-loading-inner">
                        <span style={{ fontSize: 11.5, color: 'hsl(var(--text-tertiary))', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="ai-typing-dots"><span /><span /><span /></span>
                          {mode === 'deep' ? 'Running deep analysis...' : 'Analyzing...'}
                        </span>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <div className="ai-shimmer" style={{ width: '100%' }} />
                        <div className="ai-shimmer" style={{ width: '85%' }} />
                        <div className="ai-shimmer" style={{ width: '70%' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <div className="ai-input-section">
            {/* Pending file chips — files staged for the next Send. User can
                attach up to 5 files, type a follow-up question, then submit. */}
            {pendingFiles.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }} data-testid="pending-files-list">
                {pendingFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: '#1a1a25', border: '1px solid #2a2a35', borderRadius: 8, fontSize: 12, color: '#e5e7eb', maxWidth: 260 }}>
                    <Paperclip size={12} style={{ color: '#22d3ee', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.name} <span style={{ color: '#6B7280' }}>({(f.size / 1024).toFixed(0)}KB)</span>
                    </span>
                    <button
                      onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}
                      disabled={loading}
                      data-testid={`clear-pending-file-${i}`}
                      style={{ color: '#9CA3AF', background: 'transparent', border: 0, cursor: 'pointer', padding: 0, opacity: loading ? 0.4 : 1, flexShrink: 0 }}
                      title="Buang fail ni"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {pendingFiles.length > 1 && (
                  <button
                    onClick={() => setPendingFiles([])}
                    disabled={loading}
                    data-testid="clear-all-pending-files"
                    style={{ fontSize: 11, color: '#F59E0B', background: 'transparent', border: '1px dashed #F59E0B60', borderRadius: 8, padding: '4px 8px', cursor: 'pointer' }}
                  >
                    Clear all ({pendingFiles.length})
                  </button>
                )}
              </div>
            )}
            <div className="ai-input-wrap">
              <label className="ai-upload-btn" title="Attach one or more files — type your question then hit Send" style={{ cursor: 'pointer', padding: '8px 10px', display: 'flex', alignItems: 'center', opacity: loading ? 0.5 : 1 }}>
                <Paperclip size={16} style={{ color: pendingFiles.length > 0 ? '#22d3ee' : '#9CA3AF' }} />
                <input type="file" multiple accept=".pdf,.pptx,.docx,.txt,.csv,.xlsx,.png,.jpg,.jpeg" style={{ display: 'none' }} disabled={loading}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    // Enforce a hard cap of 5 total staged files (multi-file
                    // grounding gets noisy and each is up to 20MB).
                    const currentCount = pendingFiles.length;
                    const roomLeft = Math.max(0, 5 - currentCount);
                    if (roomLeft === 0) {
                      toast.error('Max 5 fail sekali gus. Klik X untuk buang mana-mana dulu.');
                      e.target.value = '';
                      return;
                    }
                    // Client-side size check per file (backend also enforces 20MB)
                    const accepted = [];
                    for (const file of files.slice(0, roomLeft)) {
                      if (file.size > 20 * 1024 * 1024) {
                        toast.error(`"${file.name}" terlalu besar (max 20MB) — skipped`);
                        continue;
                      }
                      accepted.push(file);
                    }
                    if (accepted.length === 0) {
                      e.target.value = '';
                      return;
                    }
                    if (files.length > roomLeft) {
                      toast.warning(`Hanya ${accepted.length} fail ditambah (max 5 setiap request)`);
                    }
                    setPendingFiles(prev => [...prev, ...accepted]);
                    if (accepted.length === files.length) {
                      toast.success(`${accepted.length} fail dilampirkan — taip soalan anda dan tekan Send`);
                    }
                    e.target.value = '';
                    // Focus input so user can start typing immediately
                    setTimeout(() => inputRef.current?.focus(), 50);
                  }} />
              </label>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Tanya apa sahaja... (supports file upload & links)"
                rows={1}
                className="ai-input-field"
                onInput={(e) => { e.target.style.height = '42px'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                disabled={loading}
                data-testid="chat-input"
              />
              <button className="ai-send-btn" onClick={() => sendMessage()} disabled={(!input.trim() && pendingFiles.length === 0) || loading} data-testid="send-message-btn">
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ResponsiveLayout>
  );
}
